import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { endpoint } from "../../../utils/constants/endpoint";
import { retrieveToken } from "../../../utils/storage";

// Define a service using a base URL and expected endpoints
export const adminDocumentsApi = createApi({
  reducerPath: "adminDocuments",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["documents", "pending", "stats", "alluserdocuments"],
  endpoints: (builder) => ({
    getDocumentsData: builder.query({
      query: ({
        page: { startRow, endRow },
        userFilter = "",
        filter = {
          mainCategoryId: "",
          subCategoryId: "",
          studioId: "",
          adminId: "",
          status: "",
          companyId: "",
          practiceId: "",
          dateFrom: "",
          dateTo: "",
          referenceYear: "",
        },
        additionalFilters,
        query,
        orderDirection = "desc",
        valueToOrderBy = "createdAt",
      }) => ({
        url: `/admin/documents/list?query=${query}&userId=${userFilter}&showAll=true&mainCategoryId=${filter.mainCategoryId}&subCategoryId=${filter.subCategoryId}&status=${filter.status}&studioId=${filter.studioId}&userCompanyId=${filter.companyId}&practiceId=${filter.practiceId}&adminId=${filter.adminId}&dateFrom=${filter.dateFrom}&dateTo=${filter.dateTo}&referenceYear=${filter.referenceYear}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },

        body: {
          startRow,
          endRow,
          filter: additionalFilters,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
        },
      }),
      providesTags: ["documents"],
    }),

    getSingleDocumentId: builder.query({
      query: (id) => ({
        url: "/admin/documents/" + id,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["documents"],
    }),
    editDocument: builder.mutation({
      query: (document) => ({
        url: "/admin/documents/" + document.id,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: document,
      }),
      invalidatesTags: ["documents", "alluserdocuments"],
    }),
    createDocument: builder.mutation({
      query: (documentData) => ({
        url: "admin/documents",
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: documentData,
      }),
      invalidatesTags: ["pending"],
    }),
    deleteDocument: builder.mutation({
      query: (id) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS + "/" + id,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      invalidatesTags: ["pending", "documents"],
    }),
    deleteAllDocuments: builder.mutation({
      query: (deleteBody) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS_DELETEALL,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: deleteBody,
      }),
      invalidatesTags: ["pending", "documents"],
    }),
    rejectDocument: builder.mutation({
      query: (body) => ({
        url: "/admin/documents/" + body.id + "/reject",
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["pending", "documents", "alluserdocuments"],
    }),
    rejectAllDocument: builder.mutation({
      query: (body) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS_REJECTALL,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["pending", "documents"],
    }),
    downloadDocumentZip: builder.mutation({
      query: (data) => ({
        url: `${endpoint.ADMIN_DOCUMENT.DOCUMENTS_EXPORTZIP}?singleFiles=${data.singleFiles}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: data.ids,
      }),
    }),
    validateDocument: builder.mutation({
      query: (document) => ({
        url: `/admin/documents/${document.id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: document,
      }),
      invalidatesTags: ["pending", "documents"],
    }),
    validateAllDocument: builder.mutation({
      query: (documents) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS_VALIDATEALL,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: documents,
      }),
      invalidatesTags: ["pending", "documents"],
    }),
    reanalyzeAllDocument: builder.mutation({
      query: (documents) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS_REANALYZE_PENDINGS,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: documents,
      }),
      invalidatesTags: ["pending", "stats"],
    }),
    doCheckDuplicate: builder.mutation({
      query: (body) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS_DUPLICATE,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
    }),
    getUserStatsByUserId: builder.query({
      query: (userId) => ({
        url: endpoint.ADMIN_DOCUMENT.DOCUMENTS_STATS + userId,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["documents"],
    }),
    getAdminStats: builder.query({
      query: () => ({
        url: endpoint.ADMIN_STATS.NEW_DOC_STATS,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["stats"],
    }),
    downloadFile: builder.query({
      query: (fileUrl) => fileUrl,
      transformResponse: (response) => {
        return { data: response.blob() };
      },
    }),
    exportExcel: builder.query({
      query: (userId = "") => ({
        url: `admin/documents/export/excel?userId=${userId}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        responseHandler: async (response) => await response.blob(),
        cache: "no-cache",
      }),
    }),

    doChangeStatus: builder.mutation({
      query: (body) => ({
        url: `/admin/documents/${body.id}/setstatus`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["documents"],
    }),

    doPriorityDoc: builder.mutation({
      query: (body) => ({
        url: `/admin/documents/${body.id}/setread`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["documents"],
    }),
  }),
});

export const {
  useGetDocumentsDataQuery,
  useGetSingleDocumentIdQuery,
  useEditDocumentMutation,
  useRejectDocumentMutation,
  useRejectAllDocumentMutation,
  useDownloadDocumentZipMutation,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteAllDocumentsMutation,
  useValidateDocumentMutation,
  useValidateAllDocumentMutation,
  useReanalyzeAllDocumentMutation,
  useGetUserStatsByUserIdQuery,
  useDownloadFileQuery,
  useGetAdminStatsQuery,
  useDoCheckDuplicateMutation,
  useLazyExportExcelQuery,
  useDoChangeStatusMutation,
  useDoPriorityDocMutation
} = adminDocumentsApi;
